import { ELanguage } from './../interfaces/common';
import { observable, action } from 'mobx';
import {
  EAuthority,
  ICurrentUserInfo,
  IRole,
  IAuthrity,
  IRoleDetail,
  IUser,
  IUserDetail,
  IOutlet,
  IUserParams,
} from 'interfaces/account-manage';
import ajax from 'lib/axios';
import { IResponse } from 'interfaces/base';
import i18next from 'i18next';
import { langMap } from '@/interfaces/common';
import locale from '@/store/locale';
import { countryCode, ECountryCode } from '@/lib/locale';
import moment from 'moment';

interface IAuthTreeNode {
  value: string;
  title: string;
  [key: string]: any;
  children?: IAuthTreeNode[];
}

class AccountManage {
  @observable
  authorities: EAuthority[] = [];

  @observable
  userInfo: ICurrentUserInfo | null = null;

  @observable
  roles: IRole[] = [];

  @observable
  rolesCount = 1;

  @observable
  allAuthorities: IAuthTreeNode[] = [];

  @observable
  outlets: IOutlet[] = [];

  @observable
  outletsWithAll: IOutlet[] = [];

  @observable
  language: ELanguage =
    countryCode === ECountryCode.TH ? ELanguage.TH : countryCode === ECountryCode.TW ? ELanguage.TW : ELanguage.EN;

  getCurrentUser = async () => {
    try {
      const {
        data: { data },
      } = await ajax.get<IResponse<ICurrentUserInfo>>('/me');
      this.updateUserInfo(data);
      this.updateAuthorities(data.authorities.map(a => a.authority));
      this.changeLang(data.language);
      return data;
    } catch (e) {
      throw e;
    }
  };

  getRoles = async ({ offset, limit }: { offset?: number; limit?: number }) => {
    try {
      const {
        data: {
          data,
          extra: { totalCount },
        },
      } = await ajax.get<IResponse<IRole[], { totalCount: number }>>('/roles', { params: { offset, limit } });
      this.updateRows(data);
      this.updateRowsCount(totalCount);
    } catch (e) {}
  };

  getOutlets = async (includeCompanyId?: boolean) => {
    try {
      const {
        data: { data },
      } = await ajax.get<IResponse<IOutlet[] | null>>('/outlets', { params: { includeCompanyId } });
      if (includeCompanyId) {
        this.updateOutletsWithAll(data ?? []);
      } else {
        this.updateOutlets(data ?? []);
      }
    } catch (e) {}
  };

  getRoleDetail = async (id: string) => {
    const {
      data: { data },
    } = await ajax.get<IResponse<IRoleDetail>>('/roles/' + id);
    return data;
  };

  addNewRole = async (data: IRoleDetail) => {
    await ajax.post('/roles/add', data);
  };

  updateRole = async (data: { roleId: string } & IRoleDetail) => {
    await ajax.post('/roles/update', data);
  };

  deleteRole = async (roleId: string) => {
    return await ajax.delete(`/roles/${roleId}`, { data: { roleId } });
  };

  getAllAuths = async () => {
    try {
      const {
        data: { data },
      } = await ajax.get<IResponse<IAuthrity[]>>('/authorities');
      this.updateAllAuths(changeAuthKey(data));
    } catch (e) {}
  };

  getUsers = async (params: { offset?: number; limit?: number; username?: string; roleId?: string }) => {
    if (!params.offset) {
      params.offset = 0;
    }
    if (!params.limit) {
      params.limit = 20;
    }
    return ajax.post<IResponse<IUser[], { totalCount: number }>>('/users', params);
  };

  getUser = async (id: string) => {
    return ajax.get<IResponse<IUserDetail>>('/users/' + id);
  };

  addUser = async (params: IUserParams) => {
    return ajax.post('/users/add', params);
  };

  updateUser = async (userId: string, params: IUserParams) => {
    return ajax.post(`/users/${userId}/update`, params);
  };

  deleteUser = async (userId: string) => {
    return ajax.delete('/users/' + userId);
  };

  getAnalyticsDateFormat = () => {
    if ([ELanguage.ZH_HANT, ELanguage.TH, ELanguage.TW].includes(this.language)) {
      return 'DD/MM/YYYY';
    }
    return 'DD-MMM-YYYY';
  };

  getDateDefaultValue = () => {
    return ['DD/MM/YYYY', 'DD/MM/YYYY'];
  };

  @action
  updateUserInfo = (payload: ICurrentUserInfo) => {
    this.userInfo = payload;
  };
  @action
  updateAuthorities = (payload: EAuthority[]) => {
    this.authorities = payload;
  };
  @action
  updateRows = (payload: IRole[]) => {
    this.roles = payload;
  };
  @action
  updateRowsCount = (count: number) => {
    this.rolesCount = count;
  };
  @action
  updateAllAuths = (payload: IAuthTreeNode[]) => {
    this.allAuthorities = payload;
  };
  @action
  updateOutlets = async (payload: IOutlet[]) => {
    this.outlets = payload;
  };
  @action
  updateOutletsWithAll = async (payload: IOutlet[]) => {
    this.outletsWithAll = payload;
  };

  @action
  changeLang = (lang: ELanguage) => {
    i18next.changeLanguage(langMap[lang]);
    this.language = lang;
    locale.updateLocale(lang);
    moment.locale(langMap[lang]);
  };
}

function changeAuthKey(data: IAuthrity[]): IAuthTreeNode[] {
  return data.map(d => {
    if (d.subAuthorities) {
      return { children: changeAuthKey(d.subAuthorities), value: d.id.toString(), title: d.name };
    } else {
      return { value: d.id.toString(), title: d.name };
    }
  });
}

export default new AccountManage();
